/**
 * A ~100-line IndexedDB key/value store.
 *
 * Design §2.12 names IndexedDB twice — once for `persistQueryClient` and once
 * for the mutation outbox — so both of them share this one object store rather
 * than opening a database each. `idb-keyval` would do the same job; it is not
 * worth a dependency for one `open` and three requests, and owning the code is
 * what lets every entry point below answer "the store is unavailable" instead of
 * throwing.
 *
 * ## Unavailability is normal, not exceptional
 *
 * `indexedDB` is absent in a plain Node/jsdom run, and *present but poisoned* in
 * Safari private mode and in Firefox with cookies blocked, where `open()`
 * succeeds and then fires `onerror`. Every function here therefore resolves to a
 * sentinel (`null` / `false`) rather than rejecting, and callers degrade:
 *
 *  - the outbox keeps its entries in memory (and in `localStorage` when THAT
 *    works), so a retry still works inside the current tab;
 *  - the query persister simply never restores, and the app fetches.
 *
 * A browser-storage failure must never be able to take down a mutation. That is
 * the whole reason this file has no `throw` in it.
 */

const DB_NAME = 'crm';
const DB_VERSION = 1;
const STORE = 'kv';

/** Cached across calls; `null` once we know the store is unusable. */
let dbPromise: Promise<IDBDatabase | null> | null = null;

function factory(): IDBFactory | null {
  try {
    // `globalThis.indexedDB` throws in some sandboxed iframes rather than being
    // undefined, hence the try rather than a plain `in` check.
    return globalThis.indexedDB ?? null;
  } catch {
    return null;
  }
}

export function idbAvailable(): boolean {
  return factory() !== null;
}

function open(): Promise<IDBDatabase | null> {
  if (dbPromise !== null) return dbPromise;

  const idb = factory();
  if (idb === null) {
    dbPromise = Promise.resolve(null);
    return dbPromise;
  }

  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = idb.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    // Private-mode Firefox and a blocked upgrade both land here.
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

/**
 * The result is a DISCRIMINATED pair, not a nullable value.
 *
 * A `put` that failed and a `get` that found nothing both produce `undefined`
 * from the raw request, and collapsing them would make `idbSet` report success
 * for a write that never happened — which is exactly the bug that would let the
 * outbox claim "nothing you typed was lost" while losing it.
 */
type IdbResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false };

const FAILED: IdbResult<never> = { ok: false };

function run<T>(
  mode: IDBTransactionMode,
  body: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<IdbResult<T>> {
  return open().then(
    (db) =>
      new Promise<IdbResult<T>>((resolve) => {
        if (db === null) {
          resolve(FAILED);
          return;
        }
        let request: IDBRequest<T>;
        try {
          request = body(db.transaction(STORE, mode).objectStore(STORE));
        } catch {
          // `InvalidStateError` when the connection was closed underneath us
          // (a version change in another tab). Drop the cached handle so the
          // next call reopens.
          dbPromise = null;
          resolve(FAILED);
          return;
        }
        request.onsuccess = () => resolve({ ok: true, value: request.result });
        request.onerror = () => resolve(FAILED);
      }),
  );
}

/** `null` for "absent" AND for "could not read" — the caller treats both as no data. */
export function idbGet<T>(key: string): Promise<T | null> {
  return run<T>('readonly', (store) => store.get(key) as IDBRequest<T>).then((result) =>
    result.ok ? (result.value ?? null) : null,
  );
}

/** Resolves `false` when the write did not land, so a caller can fall back. */
export function idbSet(key: string, value: unknown): Promise<boolean> {
  return run('readwrite', (store) => store.put(value, key) as IDBRequest<unknown>).then(
    (result) => result.ok,
  );
}

export function idbDel(key: string): Promise<boolean> {
  return run('readwrite', (store) => store.delete(key) as IDBRequest<undefined>).then(
    (result) => result.ok,
  );
}

/** Test seam: forces the next call to reopen. Never called by app code. */
export function resetIdbConnection(): void {
  dbPromise = null;
}
